import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { useProfileSummary } from '../api';

import { LoginCompletionTooltip } from './LoginCompletionTooltip';
import { COMPLETION_HEALTHY_THRESHOLD } from './profileNudge.utils';

// Re-armed on each fresh sign-in by login() in features/auth/api/auth.ts —
// keep this key in sync with the sessionStorage.removeItem there.
const SEEN_KEY = 'orgProfileNudge:loginTooltipSeen';
const ORG_HUB_PATH = '/tenant-profile';

/**
 * Shows the login completion tooltip once per browser session, right after the
 * user signs in, when their Organization Profile is still incomplete. The
 * once-per-login gate uses sessionStorage so it reappears on a fresh login but
 * not on in-app navigation.
 */
export const LoginCompletionTooltipHost: FC = observer(() => {
  const navigate = useNavigate();
  const { data } = useProfileSummary();
  const [open, setOpen] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    if (!data || triggered.current) return;
    if (sessionStorage.getItem(SEEN_KEY)) return;
    if (data.progressPct >= COMPLETION_HEALTHY_THRESHOLD) return;
    triggered.current = true;
    setOpen(true);
  }, [data]);

  const dismiss = useCallback(() => {
    setOpen(false);
    sessionStorage.setItem(SEEN_KEY, '1');
  }, []);

  if (!data) return null;

  return (
    <LoginCompletionTooltip
      open={open}
      progressPct={data.progressPct}
      onComplete={() => {
        dismiss();
        navigate(ORG_HUB_PATH);
      }}
      onClose={dismiss}
    />
  );
});
